import NumberFlow from '@number-flow/react'
import type { PropsWithChildren } from 'react'
import { Fragment, useCallback, useEffect } from 'react'
import { Link } from 'react-router'
import { match, P } from 'ts-pattern'
import type { ValueOf } from 'type-fest'
import Countdown from 'src/components/Countdown'
import Text from 'src/components/ui/Text'
import { useHideMembershipBannerLoggedOutExperiment } from 'src/experiments/experiments/2605/hideMembershipBannerLoggedOut'
import { useExtraPackages } from 'src/features/ExtraPackage'
import type { GrowthSlotProps } from 'src/features/Growth/growth-slot'
import UseGrowthSlot, {
  GrowthSlot,
  GrowthSlotName,
} from 'src/features/Growth/growth-slot'
import { OrdinaryMembershipHeaderBanner } from 'src/features/Membership/components/MembershipHeaderBanner'
import useNewUserDiscount from 'src/features/Membership/hooks/useNewUserDiscount'
import { useMe } from 'src/services/helpers/user'
import { cn } from 'src/utils/cx'
import dayjs from 'src/utils/dayjs'
import useNow from 'src/utils/hooks/useNow'
import invariant from 'src/utils/invariant'
import { useRouteHandleFlag } from 'src/utils/router/route'
import { useMayTokenBeValid } from 'src/utils/token'
import { startEventGroup, track } from 'src/utils/tracker'

interface SimpleCountdownTimerProps extends StyleProps {
  targetDate: number | string | Date | dayjs.Dayjs
}

function SimpleCountdownTimer({
  targetDate,
  className,
  style,
}: SimpleCountdownTimerProps) {
  return (
    <Countdown targetDate={targetDate} utc>
      {({ timeLeft }) => (
        <span
          className={cn(
            'inline-grid grid-flow-col items-center px-1 tabular-nums text-white bg-black rounded',
            className,
          )}
          style={style}
        >
          {dayjs
            .duration(timeLeft)
            .format('D:HH:mm:ss')
            .split(':')
            .map(Number)
            .map((num, idx) =>
              idx === 0 && num === 0 ? null : (
                <Fragment key={idx}>
                  {idx > 0 && <span className="first:hidden">:</span>}
                  <NumberFlow
                    value={num}
                    trend={-1}
                    digits={idx === 0 ? undefined : { 1: { max: 5 } }}
                    format={idx === 0 ? undefined : { minimumIntegerDigits: 2 }}
                  />
                </Fragment>
              ),
            )}
        </span>
      )}
    </Countdown>
  )
}

/*
 * =================================================================================================
 */

/**
 * Banner state enum in order of priority. (From high to low)
 * The values must be unique and incremental.
 */
const BannerState = {
  NewUserDiscount: 1,
  /** A "placeholder" identifying the promoted event content to implement the priority mechanism */
  EventPromoted: 2,
  MonthlyDiscount: 3,
  /** Like `EventPromoted` but for the normal event content */
  Event: 4,
  Membership: 5,
} as const
invariant(
  Object.values(BannerState).every((value, idx) => value === idx + 1),
  'Values of BannerState should be unique and start from 1 incrementally',
)
type BannerState = ValueOf<typeof BannerState>

const MONTHLY_DISCOUNT_PROMO_THRESHOLD_DAYS = 3

/**
 * Compute the current banner state.
 * Note that the placeholder states like `EventPromoted` are not used here, as they are only FAKE/PSEUDO
 * states for marking a position in the priority order, not intended to be actually returned.
 *
 * NOTE TO EVENT DEVS:
 * If you want to dynamically control the event banner, use the `UseAppTopBannerSlot` component.
 * DO NOT modify this function since the banner state is a private business of the component.
 * See examples of `UseAppTopBannerSlot` for more details.
 */
function useBannerState(): BannerState {
  const isLoggedIn = useMayTokenBeValid()
  const newUserEndtime = useNewUserDiscount()

  const isMonthlyDiscountPromoPhase = useNow(
    useCallback(now => {
      const thresholdTime = dayjs
        .utc(now)
        .endOf('month')
        .subtract(MONTHLY_DISCOUNT_PROMO_THRESHOLD_DAYS, 'day')
      return now > thresholdTime.valueOf()
    }, []),
  )
  const creditPacks =
    useExtraPackages({ currentTier: undefined, promotionOption: {} }) ?? []
  const hasMonthlyDiscount = creditPacks.some(it => it.monthlyPurchase)

  return (
    match({
      isLoggedIn,
      newUserEndtime,
      isMonthlyDiscountPromoPhase,
      hasMonthlyDiscount,
    })
      // Only show new user discount banner when logged in
      .with(
        { isLoggedIn: true, newUserEndtime: P.string.minLength(1) },
        () => BannerState.NewUserDiscount,
      )
      // Only show monthly discount banner when user has discounted packs available to purchase
      .with(
        { isMonthlyDiscountPromoPhase: true, hasMonthlyDiscount: true },
        () => BannerState.MonthlyDiscount,
      )
      .otherwise(() => BannerState.Membership)
  )
}

/*
 * =================================================================================================
 */

export function AppTopBannerSlot(props: Omit<GrowthSlotProps, 'name'>) {
  const bannerState = useBannerState()
  const newUserEndtime = useNewUserDiscount()

  return (
    <GrowthSlot {...props} name={GrowthSlotName.AppTopBanner}>
      {match({ state: bannerState, newUserEndtime })
        .with(
          { state: BannerState.NewUserDiscount, newUserEndtime: P.nonNullable },
          ({ newUserEndtime }) => (
            <NewUserDiscountBanner targetDate={newUserEndtime} />
          ),
        )
        .with({ state: BannerState.MonthlyDiscount }, () => (
          <MonthlyDiscountBanner />
        ))
        .otherwise(() => (
          <MembershipBannerSlot />
        ))}
    </GrowthSlot>
  )
}

/**
 * Renders the ordinary membership banner, gated by the
 * `exp2605-hide-membership-banner-logged-out` experiment.
 *
 * Treatment hides the banner only when the user is logged out AND not on the
 * home page. Exposure fires only when those conditions are met so the
 * experiment population is limited to users actually affected by the change.
 */
function MembershipBannerSlot() {
  const { me, loading } = useMe()
  const routeName = useRouteHandleFlag('name')
  const isHome = routeName === 'home'
  const eligible = !loading && !me && !isHome

  const { isEnabled, isResolved, enableExposure } =
    useHideMembershipBannerLoggedOutExperiment()

  useEffect(() => {
    if (eligible) enableExposure()
  }, [eligible, enableExposure])

  // Defer rendering until the variant is known so treatment users don't see the banner flash before being hidden.
  if (eligible && !isResolved) return null
  if (eligible && isEnabled) return null
  return <OrdinaryMembershipHeaderBanner />
}

function NewUserDiscountBanner({
  targetDate,
}: {
  targetDate: number | string | Date | dayjs.Dayjs
}) {
  return (
    <div
      className="h-8 grid"
      style={{
        backgroundImage:
          'linear-gradient(to right, #ffcde8, #fffee6 30%, #cbfffd 62%, #fffee6 81%, #ffcde8 93%)',
      }}
    >
      <Link
        to="/membership/credit-packs"
        className="flex justify-center items-center font-medium no-underline whitespace-pre text-black hover:bg-black/10 transition-colors duration-100"
        onClick={() => {
          const trackingLocation = 'top-bar-newuser'
          track('enter_member_click', { location: trackingLocation })
          startEventGroup('member_source', trackingLocation, {
            persistent: true,
          })
        }}
      >
        <Text
          i18nKey="growth:credit-pack-discount-event.new-member-discount.top-banner.label"
          components={{
            countdown: <SimpleCountdownTimer targetDate={targetDate} />,
          }}
        />
      </Link>
    </div>
  )
}

function MonthlyDiscountBanner() {
  const targetDate = useNow(
    useCallback(now => dayjs.utc(now).endOf('month').valueOf(), []),
  )

  return (
    <div
      className="h-8 grid"
      style={{
        backgroundImage:
          'linear-gradient(to right, #ffcde8, #fffee6 30%, #cbfffd 62%, #fffee6 81%, #ffcde8 93%)',
      }}
    >
      <Link
        to="/membership/credit-packs"
        className="flex justify-center items-center font-medium no-underline whitespace-pre text-black hover:bg-black/10 transition-colors duration-100"
        onClick={() => {
          const trackingLocation = 'top-bar-monthly'
          track('enter_member_click', { location: trackingLocation })
          startEventGroup('member_source', trackingLocation, {
            persistent: true,
          })
        }}
      >
        <Text
          i18nKey="growth:credit-pack-discount-event.monthly-discount.top-banner.label"
          components={{
            countdown: <SimpleCountdownTimer targetDate={targetDate} />,
          }}
        />
      </Link>
    </div>
  )
}

/*
 * =================================================================================================
 */

export interface UseAppTopBannerSlotProps extends PropsWithChildren {
  /**
   * Whether to override NON-critical standard banner contents (e.g., membership
   * promotion).
   * **Caution**: DO NOT arbitrarily assign `true`, or you will always override
   * the monthly discount CTA which is not generally expected.
   */
  promoted?: boolean
}

/**
 * Inject contents to the app top banner slot (The thin banner at the very top)
 *
 * The top banner has its own standard contents and decides what to show based on a priority order.
 * E.g., New user discount > Promoted injection > Monthly discount > Normal injection
 * @see BannerState - which defines the priority order
 *
 * @example
 *   // Inject a normal content. Will ONLY show when no other discount is active.
 *   <UseAppTopBannerSlot>
 *     <p>Valentine!</p>
 *   </UseAppTopBannerSlot>
 *
 * @example
 *   // Inject a promoted content. Will OVERRIDE any content lower than BannerState.MonthlyDiscount.
 *   <UseAppTopBannerSlot promoted>
 *     <p>Valentine Closing!</p>
 *   </UseAppTopBannerSlot>
 *
 * @param promoted
 * @param children - The banner content to inject
 */
export default function UseAppTopBannerSlot({
  promoted = false,
  children,
}: UseAppTopBannerSlotProps) {
  const bannerState = useBannerState()

  return (
    <UseGrowthSlot name={GrowthSlotName.AppTopBanner}>
      {
        // Tricky part defining whether to let the event content override the standard content.
        // `children` always replaces the standard content under the slot system.
        // But sometimes we want to keep the standard content no matter `children` exists or not.
        // E.g., New user discount banner always takes the slot.
        match({ bannerState, promoted })
          // There are 2 cases when the event content should replace the standard content
          .with(
            // Case 1: When a promoted event content is injected (i.e. showing countdown timer), AND
            // no other content has higher priority needs to be shown.
            {
              bannerState: P.number.gt(BannerState.EventPromoted),
              promoted: true,
            },
            // Case 2: When a normal event content is injected, AND no other content has higher
            // priority needs to be shown.
            {
              bannerState: P.number.gt(BannerState.Event),
              promoted: false,
            },
            () => children,
          )
          // Explicitly ignore the injected content to make sure the standard content is shown.
          .otherwise(() => null)
      }
    </UseGrowthSlot>
  )
}

/* Usage example
function AppTopBannerSlotExample() {
  const ongoing = useNow(
    useCallback(
      now => dayjs.utc(now).isBetween('2026-01-21', '2026-02-03', 'day', '[]'),
      [],
    ),
  )
  const promoted = useNow(
    useCallback(now => dayjs.utc(now).isAfter('2026-01-31'), []),
  )

  return (
    <UseAppTopBannerSlot promoted={promoted}>
      {ongoing && (
        <div
          className={cn(
            'h-8 flex justify-center items-center font-quicksand font-medium text-white',
            promoted ? 'bg-rose-900' : 'bg-yellow-950',
          )}
        >
          {promoted ? 'Valentine Closing!' : 'Valentine!'}
        </div>
      )}
    </UseAppTopBannerSlot>
  )
}
 */
